import { gsap } from "animations/gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { lottieAnimation, useGsapContext } from "animations";
import { useLayoutEffect, useState } from "react";
import { isDesktop } from "utils/isDesktop";

function useLottieAnimations(
  el: any,
  exportSteps: Function,
  exportOffsets?: Function,
  selector?: boolean
) {
  const ctx = useGsapContext(el);
  const ctx2 = useGsapContext(el);

  const [lotties, _addLottie] = useState<Array<any>>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const addLottie = (lottie: any) => {
    _addLottie((lotties) => {
      let newArr = [...lotties];
      newArr[lottie.index] = lottie;
      return newArr;
    });
  };

  useLayoutEffect(() => {
    ctx.add(() => {
      const lotties = gsap.utils.toArray(".anim-lottie");
      setTotal(lotties.length);

      lottieAnimation({
        targets: lotties,
        addLottie: addLottie,
      });
    });
    return () => ctx.revert();
  }, []);

  useLayoutEffect(() => {
    !loading &&
      gsap.to(".anim-fullLoader", {
        opacity: 0,
        zIndex: -1,
        duration: 0.5,
        delay: 0.5,
        onComplete: () => gsap.killTweensOf(".anim-loader"),
      });
  }, [loading]);

  useLayoutEffect(() => {
    if (
      el.current &&
      lotties.length !== 0 &&
      lotties.length === total &&
      !lotties.includes(undefined)
    ) {
      const totalHeight = lotties.reduce(
          (partialSum, val) => partialSum + val.duration,
          0
        ),
        transitionDur = 250;

      if (document.querySelector(".anim-selector")) {
        ScrollTrigger.create({
          id: "anim-selector",
          trigger: ".anim-selector",
          pin: ".anim-selector",
          start: `top top`,
          end: () => `+=${totalHeight}px`,
          invalidateOnRefresh: true,
          pinSpacing: false,
          pinType: "fixed",
          anticipatePin: 0.5,
        });
        ScrollTrigger.create({
          id: "anim-transition",
          trigger: ".anim-selector",
          pin: ".anim-transition",
          start: `top top`,
          end: () => `+=${totalHeight}px`,
          invalidateOnRefresh: true,
          pinSpacing: false,
          pinType: "fixed",
          anticipatePin: 0.5,
        });
      }

      ctx2.add(() => {
        const masterTop = selector ? 6 * 14 + 1 : "top",
          master_tl = gsap.timeline({
            scrollTrigger: {
              trigger: el.current,
              start: `top ${masterTop}`,
              end: () => `+=${totalHeight}px`,
              scrub: isDesktop ? 1.5 : true,
              pin: true,
              pinType: "fixed",
              anticipatePin: 0.5,
              invalidateOnRefresh: true,
            },
          });

        let offsets: Array<number> = [],
          steps: Array<number> = [];

        lotties.map((lottie, index) => {
          let overallStart = 0;

          for (let i = index - 1; i >= 0; i--) {
            overallStart = overallStart + lotties[i].duration;
          }
          exportOffsets && offsets.push(overallStart);
          let dynamicSteps: any = [];
          for (let i = 0; i < lottie.markers.length; i++) {
            dynamicSteps.push(lottie.markers[i] + overallStart);
          }
          steps.push(dynamicSteps);

          // lottie animation creation
          if (total !== 1) {
            master_tl.to(lottie.wrapper, {
              opacity: 1,
              duration: transitionDur,
              ease: "none",
              delay: transitionDur * 2,
            });
          }
          master_tl.to(lottie.playhead, {
            frame: lottie.animation.totalFrames - 1,
            duration: lottie.animation.totalFrames * 16,
            ease: "none",
            onUpdate: () => {
              lottie.animation.goToAndStop(lottie.playhead.frame, true);
            },
          });

          if (total !== 1) {
            master_tl.to(lottie.wrapper, {
              yPercent: index === total - 1 ? 0 : -100,
              duration: transitionDur,
              ease: "none",
              overwrite: "auto",
            });
          }

          const markers = gsap.utils.toArray(".anim-marker", lottie.wrapper);
          markers.forEach((marker: any, i: number) => {
            const start = dynamicSteps[i];
            const end = dynamicSteps[i + 1]
              ? dynamicSteps[i + 1]
              : lottie.duration + overallStart + window.innerHeight;
            const tl: any = gsap
              .timeline({
                scrollTrigger: {
                  trigger: lottie.wrapper,
                  start: `${start}px top`,
                  end: () => `${end}px top`,
                  onEnter: () => tl.tweenFromTo(0, "mid"),
                  onLeave: () => tl.tweenTo("end"),
                  onEnterBack: () => tl.tweenFromTo("end", "mid"),
                  onLeaveBack: () => tl.tweenTo("start"),
                  invalidateOnRefresh: true,
                },
              })
              .addLabel("start")
              .fromTo(
                marker,
                { background: "#ffffffaa", height: "1rem", duration: 0.4 },
                {
                  background: "#fecc2f",
                  height: "2rem",
                  duration: 0.4,
                }
              )
              .addLabel("mid")
              .fromTo(
                marker,
                { background: "#fecc2f", height: "2rem" },
                {
                  background: "#ffffffaa",
                  height: "1rem",
                  duration: 0.4,
                }
              )
              .addLabel("end");

            gsap.set(marker, { background: "#ffffffaa", height: "1rem" });
          });

          const headlines = gsap.utils.toArray(
            ".anim-lottie-headline",
            lottie.wrapper
          );

          headlines.forEach((headline: any, i: number) => {
            const start = dynamicSteps[i];
            const end = dynamicSteps[i + 1]
              ? dynamicSteps[i + 1]
              : lottie.duration + overallStart + window.innerHeight;

            const tl: any = gsap
              .timeline({
                scrollTrigger: {
                  trigger: lottie.wrapper,
                  start: `${start}px top`,
                  end: () => `${end}px top`,
                  onEnter: () => tl.tweenFromTo(0, "mid"),
                  onLeave: () => tl.tweenTo("end"),
                  onEnterBack: () => tl.tweenFromTo("end", "mid"),
                  onLeaveBack: () => tl.tweenTo("start"),
                  invalidateOnRefresh: true,
                },
              })
              .addLabel("start")
              .scrollIn(headline)
              .addLabel("mid")
              .scrollOut(headline)
              .addLabel("end");

            gsap.set(headline, { opacity: 0, yPercent: 50 });
          });

          const firstStep = gsap.utils.toArray(
            ".anim-lottie-steps",
            lottie.wrapper
          )[0];

          const noStep = gsap.utils.toArray(".anim-no-step", lottie.wrapper);

          const animSteps = gsap.utils.toArray(
            ".anim-lottie-step",
            lottie.wrapper
          );

          animSteps.forEach((num: any, i) => {
            const index = i + noStep.length;
            const start = dynamicSteps[index],
              end = dynamicSteps[index + 1]
                ? dynamicSteps[index + 1]
                : lottie.duration + overallStart + window.innerHeight;

            const tl: any = gsap
              .timeline({
                scrollTrigger: {
                  trigger: lottie.wrapper,
                  start: `${start}px top`,
                  end: () => `${end}px top`,
                  onEnter: () => tl.tweenFromTo(0, "mid"),
                  onLeave: () => tl.tweenTo("end"),
                  onEnterBack: () => tl.tweenFromTo("end", "mid"),
                  onLeaveBack: () => tl.tweenTo("start"),
                  invalidateOnRefresh: true,
                },
              })
              .addLabel("start")
              .fadeIn(i === 0 ? firstStep : num)
              .addLabel("mid")
              .fadeOut(i === animSteps.length - 2 ? firstStep : num)
              .addLabel("end");

            gsap.set(num, { opacity: 0 });
          });
          firstStep && gsap.set(firstStep, { opacity: 0 });

          return null;
        });

        exportSteps && exportSteps({ steps });
        exportOffsets && exportOffsets({ offsets });
      });

      ScrollTrigger.sort();
      ScrollTrigger.refresh();

      setLoading(false);
      return () => {
        ScrollTrigger.getById("anim-selector")?.kill(true);
        ScrollTrigger.getById("anim-transition")?.kill(true);
        ctx2.revert();
      };
    }
  }, [lotties]);
}

export default useLottieAnimations;
